import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TopNav from '../TopNav';
import { getPortfolio, getTrades, getPortfolioPositions } from '../../services/api';
import api from '../../services/api';
import PnLGraphModal from './PnLGraphModal';
import WinRateGraphModal from './WinRateGraphModal';
import UserMetricsModal from './UserMetricsModal';

interface PortfolioData {
  totalValue: number;
  totalPnL: number;
  totalPnLPercent: number;
  pnlDay: number;
  pnlWeek: number;
  pnlMonth: number;
  pnlTotal: number;
  cash: number;
  buyingPower: number;
  dayTradeCount: number;
  positions: Position[];
}

interface Position {
  symbol: string;
  quantity: number;
  averagePrice: number;
  currentPrice: number;
  marketValue: number;
  unrealizedPnL: number;
  unrealizedPnLPercent: number;
}

interface TradeSummary {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  totalVolume: number;
  averageTradeSize: number;
}

interface WinRateData {
  day: { winRate: number; winningTrades: number; losingTrades: number };
  week: { winRate: number; winningTrades: number; losingTrades: number };
  month: { winRate: number; winningTrades: number; losingTrades: number };
  total: { winRate: number; winningTrades: number; losingTrades: number };
}

/**
 * Main Dashboard Component
 * Displays portfolio overview, positions, and quick actions
 */
const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [portfolioData, setPortfolioData] = useState<PortfolioData | null>(null);
  const [tradeSummary, setTradeSummary] = useState<TradeSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heartbeat, setHeartbeat] = useState<any | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [hasActiveAlpaca, setHasActiveAlpaca] = useState<boolean>(false);
  const [pnlPeriod, setPnlPeriod] = useState<'day' | 'week' | 'month' | 'total'>('total');
  const [winRatePeriod, setWinRatePeriod] = useState<'day' | 'week' | 'month' | 'total'>('total');
  const [winRateData, setWinRateData] = useState<WinRateData>({
    day: { winRate: 0, winningTrades: 0, losingTrades: 0 },
    week: { winRate: 0, winningTrades: 0, losingTrades: 0 },
    month: { winRate: 0, winningTrades: 0, losingTrades: 0 },
    total: { winRate: 0, winningTrades: 0, losingTrades: 0 }
  });
  const [showPnLModal, setShowPnLModal] = useState(false);
  const [showWinRateModal, setShowWinRateModal] = useState(false);
  const [showUserMetricsModal, setShowUserMetricsModal] = useState(false);
  const [userMetricsType, setUserMetricsType] = useState<'portfolio' | 'cash' | 'buyingPower' | 'trades'>('portfolio');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // For admin users, fetch combined data from admin metrics
      if (user?.role === 'ADMIN') {
        const adminResponse = await api.get('/admin/metrics');
        const adminData = adminResponse.data?.data;
        const summary = adminData?.portfolioSummary || {};
        
        setPortfolioData({
          totalValue: typeof summary.totalValue === 'number' ? summary.totalValue : 0,
          totalPnL: typeof summary.pnlTotal === 'number' ? summary.pnlTotal : 0,
          totalPnLPercent: summary.pnlTotal && summary.totalValue ? (summary.pnlTotal / summary.totalValue) * 100 : 0,
          pnlDay: typeof summary.pnlDay === 'number' ? summary.pnlDay : 0,
          pnlWeek: typeof summary.pnlWeek === 'number' ? summary.pnlWeek : 0,
          pnlMonth: typeof summary.pnlMonth === 'number' ? summary.pnlMonth : 0,
          pnlTotal: typeof summary.pnlTotal === 'number' ? summary.pnlTotal : 0,
          cash: typeof summary.cash === 'number' ? summary.cash : 0,
          buyingPower: typeof summary.buyingPower === 'number' ? summary.buyingPower : 0,
          dayTradeCount: typeof summary.dayTradeCount === 'number' ? summary.dayTradeCount : 0,
          positions: [], // Admin view doesn't show individual positions
        });
      } else {
        // Regular user - fetch individual portfolio data
        const [portfolioResponse, positionsResponse, accountsResponse] = await Promise.all([
          getPortfolio(),
          getPortfolioPositions(),
          api.get('/accounts')
        ]);
        const summary = (portfolioResponse && portfolioResponse.data) || {} as any;
        const positions = Array.isArray(positionsResponse?.data) ? positionsResponse.data : [];
        const accounts = Array.isArray(accountsResponse?.data?.data) ? accountsResponse.data.data : [];
        const active = accounts.some((a: any) => (a.status === 'ACTIVE') && a.alpacaKey && a.alpacaSecret);
        setHasActiveAlpaca(active);
        // Backend returns pnlTotal, pnlDay, pnlWeek, pnlMonth (not totalPnL)
        const pnlTotal = typeof summary.pnlTotal === 'number' ? summary.pnlTotal : 0;
        const totalValue = typeof summary.totalValue === 'number' ? summary.totalValue : 0;
        const totalPnLPercent = totalValue > 0 ? (pnlTotal / totalValue) * 100 : 0;
        
        setPortfolioData({
          totalValue,
          totalPnL: pnlTotal, // Map pnlTotal to totalPnL for consistency
          totalPnLPercent,
          pnlDay: typeof summary.pnlDay === 'number' ? summary.pnlDay : 0,
          pnlWeek: typeof summary.pnlWeek === 'number' ? summary.pnlWeek : 0,
          pnlMonth: typeof summary.pnlMonth === 'number' ? summary.pnlMonth : 0,
          pnlTotal,
          cash: typeof summary.cash === 'number' ? summary.cash : 0,
          buyingPower: typeof summary.buyingPower === 'number' ? summary.buyingPower : 0,
          dayTradeCount: typeof summary.dayTradeCount === 'number' ? summary.dayTradeCount : 0,
          positions,
        });
      }
      
      // Fetch trades data for different periods to calculate Win Rate
      const now = new Date();
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - 7);
      
      const monthStart = new Date(now);
      monthStart.setMonth(now.getMonth() - 1);

      // For admin users, use admin-specific endpoints that aggregate across all users
      if (user?.role === 'ADMIN') {
        console.log('[Dashboard] Fetching admin trades summary...');
        const [tradesTotal, tradesDay, tradesWeek, tradesMonth] = await Promise.all([
          api.get('/admin/trades-summary'),
          api.get(`/admin/trades-summary?startDate=${dayStart.toISOString()}`),
          api.get(`/admin/trades-summary?startDate=${weekStart.toISOString()}`),
          api.get(`/admin/trades-summary?startDate=${monthStart.toISOString()}`)
        ]);

        console.log('[Dashboard] Admin trades-summary response:', {
          success: tradesTotal?.data?.success,
          hasData: !!tradesTotal?.data?.data,
          hasSummary: !!tradesTotal?.data?.data?.summary,
          summary: tradesTotal?.data?.data?.summary
        });

        // Set trade summary (overall)
        const totalSummary = tradesTotal?.data?.data?.summary || {};
        
        // Debug: Check if summary has data
        if (Object.keys(totalSummary).length > 0) {
          console.log('[Dashboard] Setting trade summary:', totalSummary);
          setTradeSummary({
            totalTrades: totalSummary.totalTrades || 0,
            winningTrades: totalSummary.winningTrades || 0,
            losingTrades: totalSummary.losingTrades || 0,
            totalVolume: totalSummary.totalVolume || 0,
            averageTradeSize: totalSummary.averageTradeSize || 0
          });
        } else {
          console.warn('[Dashboard] Trade summary is empty:', {
            fullResponse: tradesTotal?.data,
            dataPath: tradesTotal?.data?.data,
            summaryPath: tradesTotal?.data?.data?.summary
          });
          // Set fallback
          setTradeSummary({
            totalTrades: 0,
            winningTrades: 0,
            losingTrades: 0,
            totalVolume: 0,
            averageTradeSize: 0
          });
        }

        // Calculate Win Rate for each period
        const calculateWinRate = (response: any) => {
          const summary = response?.data?.data?.summary || {};
          const winningTrades = summary.winningTrades || 0;
          const losingTrades = summary.losingTrades || 0;
          const totalTrades = winningTrades + losingTrades;
          const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
          return {
            winRate,
            winningTrades,
            losingTrades
          };
        };

        setWinRateData({
          day: calculateWinRate(tradesDay),
          week: calculateWinRate(tradesWeek),
          month: calculateWinRate(tradesMonth),
          total: calculateWinRate(tradesTotal)
        });
      } else {
        // Regular users - use user-specific endpoints
        const [tradesTotal, tradesDay, tradesWeek, tradesMonth] = await Promise.all([
          getTrades(), // All time
          api.get(`/trades?startDate=${dayStart.toISOString()}`),
          api.get(`/trades?startDate=${weekStart.toISOString()}`),
          api.get(`/trades?startDate=${monthStart.toISOString()}`)
        ]);

        // Set trade summary (overall)
        // API response structure: { success: true, data: { trades: [], summary: {}, pagination: {} } }
        const totalSummary = (tradesTotal as any).data?.data?.summary || (tradesTotal as any).data?.summary || {};
        if (totalSummary) {
          setTradeSummary({
            totalTrades: totalSummary.totalTrades || 0,
            winningTrades: totalSummary.winningTrades || 0,
            losingTrades: totalSummary.losingTrades || 0,
            totalVolume: totalSummary.totalVolume || 0,
            averageTradeSize: totalSummary.averageTradeSize || 0
          });
        }

        // Calculate Win Rate for each period
        // API response structure: { success: true, data: { trades: [], summary: {}, pagination: {} } }
        const calculateWinRate = (response: any) => {
          const summary = response?.data?.data?.summary || response?.data?.summary || {};
          const winningTrades = summary.winningTrades || 0;
          const losingTrades = summary.losingTrades || 0;
          const totalTrades = winningTrades + losingTrades;
          const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
          return {
            winRate,
            winningTrades,
            losingTrades
          };
        };

        setWinRateData({
          day: calculateWinRate(tradesDay),
          week: calculateWinRate(tradesWeek),
          month: calculateWinRate(tradesMonth),
          total: calculateWinRate(tradesTotal)
        });
      }

      // Automation heartbeat (last/next run + schedule) - non-fatal
      try {
        const hbResp = await api.get('/automation/heartbeat');
        if (hbResp?.data?.success) {
          setHeartbeat(hbResp.data.data);
        }
      } catch (e) {
        // Ignore heartbeat errors; UI will show Idle
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
      
      // Set fallback data if API fails
      setPortfolioData({
        totalValue: 0,
        totalPnL: 0,
        totalPnLPercent: 0,
        pnlDay: 0,
        pnlWeek: 0,
        pnlMonth: 0,
        pnlTotal: 0,
        cash: 0,
        buyingPower: 0,
        dayTradeCount: 0,
        positions: []
      });
      setTradeSummary({
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        totalVolume: 0,
        averageTradeSize: 0
      });
    } finally {
      setIsLoading(false);
    }
  };
  // countdown ticker for next run
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const renderCountdown = (nextRunAt?: string | Date | null) => {
    if (!nextRunAt) return '—';
    const next = new Date(nextRunAt).getTime();
    const ms = Math.max(0, next - now);
    const s = Math.floor(ms / 1000);
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };


  const formatCurrency = (amount?: number): string => {
    const safe = typeof amount === 'number' && isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(safe);
  };

  const formatPercentage = (value?: number): string => {
    const safe = typeof value === 'number' && isFinite(value) ? value : 0;
    return `${safe >= 0 ? '+' : ''}${safe.toFixed(2)}%`;
  };

  const getPnLColor = (value: number): string => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-neutral-400';
  };

  const getPnLBgColor = (value: number): string => {
    if (value > 0) return 'bg-green-900/20 border-green-500/50';
    if (value < 0) return 'bg-red-900/20 border-red-500/50';
    return 'bg-neutral-900/20 border-neutral-500/50';
  };

  const handleCardClick = (type: 'portfolio' | 'cash' | 'buyingPower' | 'trades') => {
    if (user?.role === 'ADMIN') {
      setUserMetricsType(type);
      setShowUserMetricsModal(true);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Dashboard</h1>
            <p className="text-sm sm:text-base text-neutral-400">
              Welcome back, {user?.email} • Last updated {new Date().toLocaleTimeString()}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-3 sm:px-4 py-2 sm:py-3 rounded-lg mb-4 sm:mb-6 text-sm sm:text-base">
              {error}
            </div>
          )}

          {/* Portfolio Overview Cards */}
          {portfolioData && (
            <>
              {/* Show Alpaca setup guidance for users with no portfolio data */}
              {portfolioData.totalValue === 0 && user?.role !== 'ADMIN' && !hasActiveAlpaca && (
                <div className="mb-4 sm:mb-6 bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-blue-500/50 rounded-lg p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start">
                    <div className="flex-shrink-0 mb-3 sm:mb-0">
                      <svg className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="sm:ml-4 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-blue-400 mb-2">Ready to Start Trading?</h3>
                      <div className="text-xs sm:text-sm text-blue-300 space-y-2">
                        <p>Your portfolio shows $0.00 because you haven't connected your Alpaca account yet. To begin automated trading, you need to:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2 sm:ml-4">
                          <li>Create a free account at <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">alpaca.markets</a></li>
                          <li>Get your API keys from the Alpaca dashboard</li>
                          <li>Add them to your account here to start trading</li>
                        </ol>
                      </div>
                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <Link to="/alpaca-keys" className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-3 rounded-lg font-medium transition-colors text-center min-h-touch">
                          Add Alpaca Keys
                        </Link>
                        <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors text-center min-h-touch">
                          Create Alpaca Account
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
              {/* Total Portfolio Value */}
              <button
                onClick={() => handleCardClick('portfolio')}
                disabled={user?.role !== 'ADMIN'}
                className={`bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-left w-full ${
                  user?.role === 'ADMIN' ? 'cursor-pointer hover:border-cyan-500/50 hover:bg-neutral-800/50 transition-all' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Total Portfolio</p>
                    <p className="text-xl font-bold text-white">
                      {formatCurrency(portfolioData.totalValue)}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-cyan-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
                {user?.role === 'ADMIN' && (
                  <p className="text-xs text-neutral-400 mt-2 text-center">Click to view by user</p>
                )}
              </button>

              {/* P&L with Period Selector - Clickable */}
              <button
                onClick={() => setShowPnLModal(true)}
                className={`bg-neutral-900 border rounded-lg p-3 sm:p-4 cursor-pointer hover:border-cyan-500/50 transition-all text-left w-full ${getPnLBgColor(
                  pnlPeriod === 'day' ? portfolioData.pnlDay :
                  pnlPeriod === 'week' ? portfolioData.pnlWeek :
                  pnlPeriod === 'month' ? portfolioData.pnlMonth :
                  portfolioData.pnlTotal
                )}`}
              >
                <div>
                <div className="mb-2">
                  <div className="flex flex-wrap gap-1 mb-2">
                    <button
                      onClick={() => setPnlPeriod('day')}
                      className={`px-2 sm:px-2.5 py-1 text-[10px] sm:text-xs rounded transition-colors ${
                        pnlPeriod === 'day'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 active:bg-neutral-600'
                      }`}
                    >
                      Day
                    </button>
                    <button
                      onClick={() => setPnlPeriod('week')}
                      className={`px-2 sm:px-2.5 py-1 text-[10px] sm:text-xs rounded transition-colors ${
                        pnlPeriod === 'week'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 active:bg-neutral-600'
                      }`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setPnlPeriod('month')}
                      className={`px-2 sm:px-2.5 py-1 text-[10px] sm:text-xs rounded transition-colors ${
                        pnlPeriod === 'month'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 active:bg-neutral-600'
                      }`}
                    >
                      Month
                    </button>
                    <button
                      onClick={() => setPnlPeriod('total')}
                      className={`px-2 sm:px-2.5 py-1 text-[10px] sm:text-xs rounded transition-colors ${
                        pnlPeriod === 'total'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 active:bg-neutral-600'
                      }`}
                    >
                      Total
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">
                      {pnlPeriod === 'day' ? 'Daily' : 
                       pnlPeriod === 'week' ? 'Weekly' : 
                       pnlPeriod === 'month' ? 'Monthly' : 'Total'} P&L
                    </p>
                    <p className={`text-xl font-bold ${getPnLColor(
                      pnlPeriod === 'day' ? portfolioData.pnlDay :
                      pnlPeriod === 'week' ? portfolioData.pnlWeek :
                      pnlPeriod === 'month' ? portfolioData.pnlMonth :
                      portfolioData.pnlTotal
                    )}`}>
                      {formatCurrency(
                        pnlPeriod === 'day' ? portfolioData.pnlDay :
                        pnlPeriod === 'week' ? portfolioData.pnlWeek :
                        pnlPeriod === 'month' ? portfolioData.pnlMonth :
                        portfolioData.pnlTotal
                      )}
                    </p>
                    <p className={`text-xs ${getPnLColor(
                      ((pnlPeriod === 'day' ? portfolioData.pnlDay :
                        pnlPeriod === 'week' ? portfolioData.pnlWeek :
                        pnlPeriod === 'month' ? portfolioData.pnlMonth :
                        portfolioData.pnlTotal) / portfolioData.totalValue) * 100
                    )}`}>
                      {formatPercentage(
                        portfolioData.totalValue > 0
                          ? ((pnlPeriod === 'day' ? portfolioData.pnlDay :
                              pnlPeriod === 'week' ? portfolioData.pnlWeek :
                              pnlPeriod === 'month' ? portfolioData.pnlMonth :
                              portfolioData.pnlTotal) / portfolioData.totalValue) * 100
                          : 0
                      )}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-neutral-600/20 rounded-lg flex items-center justify-center group-hover:bg-cyan-600/30 transition-colors">
                    <svg className="h-5 w-5 text-neutral-400 group-hover:text-cyan-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
                </div>
                <p className="text-xs text-neutral-400 mt-2 text-center">Click to view P&L graph</p>
              </button>

              {/* Cash */}
              <button
                onClick={() => handleCardClick('cash')}
                disabled={user?.role !== 'ADMIN'}
                className={`bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-left w-full ${
                  user?.role === 'ADMIN' ? 'cursor-pointer hover:border-cyan-500/50 hover:bg-neutral-800/50 transition-all' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Cash</p>
                    <p className="text-xl font-bold text-white">
                      {formatCurrency(portfolioData.cash)}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                </div>
                {user?.role === 'ADMIN' && (
                  <p className="text-xs text-neutral-400 mt-2 text-center">Click to view by user</p>
                )}
              </button>

              {/* Buying Power */}
              <button
                onClick={() => handleCardClick('buyingPower')}
                disabled={user?.role !== 'ADMIN'}
                className={`bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-left w-full ${
                  user?.role === 'ADMIN' ? 'cursor-pointer hover:border-cyan-500/50 hover:bg-neutral-800/50 transition-all' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Buying Power</p>
                    <p className="text-xl font-bold text-white">
                      {formatCurrency(portfolioData.buyingPower)}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                {user?.role === 'ADMIN' && (
                  <p className="text-xs text-neutral-400 mt-2 text-center">Click to view by user</p>
                )}
              </button>

              {/* Automation Heartbeat */}
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Automation</p>
                    <p className="text-xl font-bold text-white">
                      {(() => {
                        const isRunning = heartbeat?.orderStatusUpdate?.status === 'RUNNING' || heartbeat?.marketScreening?.status === 'RUNNING';
                        const recentlyExecuted = heartbeat?.marketScreening?.lastRunAt && 
                          new Date(heartbeat.marketScreening.lastRunAt) > new Date(Date.now() - 5 * 60 * 1000);
                        return isRunning || recentlyExecuted ? 'Running' : 'Idle';
                      })()}
                    </p>
                    <p className="text-xs text-neutral-400">
                      Last run: {heartbeat?.marketScreening?.lastRunAt ? new Date(heartbeat.marketScreening.lastRunAt).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET' : '—'}
                    </p>
                    <p className="text-xs text-neutral-400">
                      Next run: {heartbeat?.marketScreening?.nextRunAt ? new Date(heartbeat.marketScreening.nextRunAt).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET' : '—'}
                      {heartbeat?.marketScreening?.nextRunAt && (
                        <span className="ml-2 text-neutral-500">({renderCountdown(heartbeat.marketScreening.nextRunAt)})</span>
                      )}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-neutral-600/20 rounded-lg flex items-center justify-center">
                    <span className={`h-2.5 w-2.5 rounded-full ${heartbeat?.isInitialized ? 'bg-emerald-400' : 'bg-yellow-400'}`}></span>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}

          {/* Trading Statistics */}
          {tradeSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <button
                onClick={() => handleCardClick('trades')}
                disabled={user?.role !== 'ADMIN'}
                className={`bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-left w-full ${
                  user?.role === 'ADMIN' ? 'cursor-pointer hover:border-cyan-500/50 hover:bg-neutral-800/50 transition-all' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Total Trades</p>
                    <p className="text-xl font-bold text-white">{tradeSummary.totalTrades}</p>
                  </div>
                  <div className="h-10 w-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                {user?.role === 'ADMIN' && (
                  <p className="text-xs text-neutral-400 mt-2 text-center">Click to view by user</p>
                )}
              </button>

              <button
                onClick={() => setShowWinRateModal(true)}
                className="bg-neutral-900 border border-neutral-700 rounded-lg p-4 cursor-pointer hover:border-cyan-500/50 transition-all text-left w-full group"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-neutral-400">Win Rate</p>
                  <div className="flex gap-1 bg-neutral-800 rounded-lg p-0.5">
                    <button
                      onClick={() => setWinRatePeriod('day')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                        winRatePeriod === 'day'
                          ? 'bg-cyan-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Day
                    </button>
                    <button
                      onClick={() => setWinRatePeriod('week')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                        winRatePeriod === 'week'
                          ? 'bg-cyan-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setWinRatePeriod('month')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                        winRatePeriod === 'month'
                          ? 'bg-cyan-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Month
                    </button>
                    <button
                      onClick={() => setWinRatePeriod('total')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                        winRatePeriod === 'total'
                          ? 'bg-cyan-600 text-white'
                          : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      Total
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-white">
                      {winRateData[winRatePeriod].winRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {winRateData[winRatePeriod].winningTrades}W / {winRateData[winRatePeriod].losingTrades}L
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-green-600/20 rounded-lg flex items-center justify-center group-hover:bg-cyan-600/30 transition-colors">
                    <svg className="h-5 w-5 text-green-400 group-hover:text-cyan-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-neutral-400 mt-2 text-center">Click to view win rate analysis</p>
              </button>

              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Total Volume</p>
                    <p className="text-xl font-bold text-white">
                      {formatCurrency(tradeSummary.totalVolume)}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-neutral-400">Avg Trade Size</p>
                    <p className="text-xl font-bold text-white">
                      {formatCurrency(tradeSummary.averageTradeSize)}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-indigo-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Active Positions */}
          <div className="mb-6 sm:mb-8">
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-700">
                <h2 className="text-base sm:text-lg font-semibold text-white">Active Positions</h2>
              </div>
              <div className="p-4 sm:p-6">
                {portfolioData && portfolioData.positions.length > 0 ? (
                  <div className="space-y-3 sm:space-y-4">
                    {portfolioData.positions.map((position) => (
                      <div key={position.symbol} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-neutral-800 rounded-lg gap-3 sm:gap-0">
                        <div className="flex items-center space-x-3 sm:space-x-4">
                          <div>
                            <h3 className="text-sm sm:text-base font-semibold text-white">{position.symbol}</h3>
                            <p className="text-xs sm:text-sm text-neutral-400">
                              {position.quantity} shares @ {formatCurrency(position.averagePrice)}
                            </p>
                            <p className="text-xs text-neutral-500 mt-0.5">
                              Current: {formatCurrency(position.currentPrice)}
                            </p>
                          </div>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-sm sm:text-base font-semibold text-white">
                            {formatCurrency(position.marketValue)}
                          </p>
                          <p className={`text-xs sm:text-sm ${getPnLColor(position.unrealizedPnL)}`}>
                            <span className="text-neutral-400">Unrealized P&L: </span>
                            {formatCurrency(position.unrealizedPnL)} ({formatPercentage(position.unrealizedPnLPercent)})
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-neutral-400 mb-4">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">No active positions</h3>
                    <p className="text-neutral-400">Start trading to see your positions here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PDT Warning */}
          {portfolioData && portfolioData.dayTradeCount >= 3 && (
            <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-3 sm:p-4 mb-6 sm:mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-xs sm:text-sm font-medium text-yellow-400">PDT Warning</h3>
                  <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-yellow-300">
                    <p>
                      You have made {portfolioData.dayTradeCount} day trades today. 
                      Pattern Day Trader rules may apply.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <PnLGraphModal
        isOpen={showPnLModal}
        onClose={() => setShowPnLModal(false)}
        isAdmin={user?.role === 'ADMIN'}
      />
      <WinRateGraphModal
        isOpen={showWinRateModal}
        onClose={() => setShowWinRateModal(false)}
        isAdmin={user?.role === 'ADMIN'}
      />
      {user?.role === 'ADMIN' && (
        <UserMetricsModal
          isOpen={showUserMetricsModal}
          onClose={() => setShowUserMetricsModal(false)}
          metricType={userMetricsType}
        />
      )}
    </div>
  );
};

export default Dashboard;
